import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Pencil, Trash2, Star, Search, Plus, MoreVertical } from 'lucide-react';
import { useUserContext } from '../../contexts/UserContext';
import { updateSpecificContentfulIntegration } from '../../utils/APIs';
import Loader from '../UI/Loader/Loader';
import DeleteModal from '../UI/Modal/DeleteModal';
import { formatDate } from '../../utils/converter';
import { toast } from 'react-toastify';
import DeleteIntegrationModal from './DeleteIntegrationModal';
import { useContentfulContext } from '../../contexts/ContentfulContext';
import { getAgGridSortStorageKey } from '../UI/AgGrid';
import useComponentVisible from '../UI/ComponentVisible';
import { Card, Button, Input, AgTableCard } from '../UI';

const ContentfulIntegrationTable = () => {
    const navigate = useNavigate();
    const { content } = useContentfulContext();

    const { userData, updateContentfulIntegrations } = useUserContext();
    const [deleteIntegration, setDeleteIntegration] = useState(false);
    const [data, setData] = useState([]);
    const [loading] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [handleId, setHandleId] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [confirmModal, setConfirmModal] = useState({
        active: false,
        deactivate: false,
        delete: false
    });
    const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
    const { ref, isComponentVisible, setIsComponentVisible } = useComponentVisible(false);

    useEffect(() => {
        setData(userData?.contentfulIntegrations || []);
    }, [userData?.contentfulIntegrations]);

    const [gridApi, setGridApi] = useState(null);

    const onFilterTextBoxChanged = (e) => {
        const val = e.target.value;
        setSearchTerm(val);
        if (gridApi) {
            gridApi.setFilterModel({
                name: val ? { type: 'contains', filter: val.toLowerCase() } : null
            });
        }
    };

    const handleClearSearch = () => {
        setSearchTerm('');
        if (gridApi) {
            gridApi.setFilterModel({});
        }
    };

    const handleSetAsDefault = async (id) => {
        try {
            setActionLoading(true);
            await updateSpecificContentfulIntegration(
                userData.token,
                userData.selectedOrganization.org_token,
                { integrationId: id, bodyData: { default: true } }
            );
            updateContentfulIntegrations();
            toast.success(content.CONTENTFUL_CONTENT.success.update);
        } catch (error) {
            toast.error(error.message);
        } finally {
            setActionLoading(false);
        }
    };

    const colDefs = [
        {
            headerName: content.CONTENTFUL_CONTENT.integration_table_headers.integration_name,
            field: "name",
            cellRenderer: ({ data }) => (
                <button
                    type="button"
                    className="font-medium text-primary hover:text-primary-hover hover:underline text-left"
                    onClick={() => navigate(`/setup/edit-contentful-integration/${data._id}`)}
                >
                    {data.name}
                </button>
            ),
            filter: true,
            sortable: true,
            suppressMovable: true,
        },
        {
            headerName: content.CONTENTFUL_CONTENT.integration_table_headers.environments,
            field: "environments",
            cellRenderer: ({ data }) => {
                const envs = data.environments || [];
                if (envs.length === 0) return <span className="text-stride-muted">—</span>;
                return (
                    <div className="flex flex-wrap gap-1.5 py-1">
                        {envs.map((env, i) => (
                            <span key={i} className="stride-badge-pill bg-slate-100 text-slate-700 text-xs font-mono">
                                {env}
                            </span>
                        ))}
                    </div>
                );
            },
            suppressMovable: true,
        },
        {
            headerName: content.CONTENTFUL_CONTENT.integration_table_headers.default,
            field: "default",
            sortable: false,
            cellRenderer: ({ data }) => {
                const isDefault = data.default === true || data.default === "true";
                return isDefault ? (
                    <span className="stride-badge-pill bg-emerald-50 text-emerald-700 font-semibold text-xs">
                        Default
                    </span>
                ) : (
                    <span className="text-stride-muted text-xs">—</span>
                );
            },
            suppressMovable: true
        },
        {
            headerName: content.CONTENTFUL_CONTENT.integration_table_headers.updated_at,
            field: "updatedAt",
            cellRenderer: ({ data }) => (
                <span className="text-stride-muted text-xs">
                    {formatDate(data.updatedAt)}
                </span>
            ),
            comparator: (date1, date2) => new Date(date1) - new Date(date2),
            sortable: true,
            suppressMovable: true
        },
        {
            headerName: 'Actions',
            field: 'actions',
            width: 140,
            sortable: false,
            filter: false,
            resizable: false,
            suppressMovable: true,
            cellRenderer: ({ data }) => {
                const isDefault = data.default === true || data.default === "true";
                return (
                    <div className="stride-table-actions">
                        <button
                            type="button"
                            title="Edit Integration"
                            className="stride-table-btn stride-table-btn-edit"
                            onClick={() => navigate(`/setup/edit-contentful-integration/${data._id}`)}
                        >
                            <Pencil className="w-3.5 h-3.5" />
                        </button>
                        {!isDefault && (
                            <button
                                type="button"
                                title="Set as default"
                                className="stride-table-btn hover:text-amber-600 hover:border-amber-300"
                                onClick={() => handleSetAsDefault(data._id)}
                            >
                                <Star className="w-3.5 h-3.5 text-amber-500" />
                            </button>
                        )}
                        <button
                            type="button"
                            title="Delete Integration"
                            className="stride-table-btn stride-table-btn-delete"
                            onClick={() => {
                                setHandleId(data._id);
                                setDeleteIntegration(true);
                            }}
                        >
                            <Trash2 className="w-3.5 h-3.5" />
                        </button>
                        <button
                            type="button"
                            className="stride-table-btn actions"
                            title="More options"
                            onClick={(e) => {
                                e.stopPropagation();
                                setHandleId(data._id);
                                setIsComponentVisible(!isComponentVisible);
                                const buttonRect = e.currentTarget.getBoundingClientRect();
                                setMenuPosition({
                                    top: buttonRect.bottom + window.scrollY,
                                    left: buttonRect.left + window.scrollX
                                });
                            }}
                        >
                            <MoreVertical className="w-3.5 h-3.5" />
                        </button>
                    </div>
                );
            },
        },
    ];

    const onGridReady = (params) => {
        setGridApi(params.api);

        const storageKey = getAgGridSortStorageKey("contentful-integration-table");
        const sortModel = JSON.parse(localStorage.getItem(storageKey));

        if (!sortModel || sortModel?.length === 0) {
            params.columnApi.applyColumnState({
                state: [
                    { colId: 'updatedAt', sort: 'desc' }
                ],
                applyOrder: false,
            });
        }
    };

    const autoSizeStrategy = useMemo(() => {
        return {
            type: "fitGridWidth",
            defaultMinWidth: 40,
            columnLimits: [
                {
                    colId: "name",
                    minWidth: 260,
                },
                {
                    colId: "environments",
                    minWidth: 240,
                },
                {
                    colId: "default",
                    minWidth: 120,
                },
                {
                    colId: "updatedAt",
                    minWidth: 160,
                },
                {
                    colId: "actions",
                    minWidth: 140,
                }
            ],
        };
    }, []);

    return (
        <div className="flex flex-col space-y-6">
            {/* Empty State */}
            {!loading && (!data || data.length === 0) ? (
                <Card className="text-center p-12 max-w-lg mx-auto">
                    <p className="text-base text-stride-heading font-medium mb-4">
                        {content.CONTENTFUL_CONTENT.error.FE133}
                    </p>
                    <Button
                        variant="primary"
                        icon={<Plus className="w-4 h-4" />}
                        onClick={() => navigate('/setup/add-contentful-integration')}
                    >
                        {content.CONTENTFUL_CONTENT.new_integration_cta_text}
                    </Button>
                </Card>
            ) : (
                <AgTableCard
                    header={
                        <>
                            <div className="w-full md:w-80">
                                <Input
                                    placeholder="Search integration name..."
                                    value={searchTerm}
                                    onChange={onFilterTextBoxChanged}
                                    prefixIcon={<Search className="w-4 h-4 text-stride-muted" />}
                                    clearable
                                    onClear={handleClearSearch}
                                />
                            </div>

                            <Button
                                variant="primary"
                                icon={<Plus className="w-4 h-4" />}
                                onClick={() => navigate('/setup/add-contentful-integration')}
                            >
                                {content.CONTENTFUL_CONTENT.new_integration_cta_text}
                            </Button>
                        </>
                    }
                    tableId="contentful-integration-table"
                    loading={loading}
                    rowData={data}
                    columnDefs={colDefs}
                    autoSizeStrategy={autoSizeStrategy}
                    onGridReady={onGridReady}
                >
                    {isComponentVisible && (
                        <div
                            ref={ref}
                            className="absolute z-10 mt-2 w-48 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none py-1"
                            style={{ top: menuPosition.top, left: Math.max(10, menuPosition.left - 180) }}
                        >
                            <div className="py-1">
                                <a
                                    href="#"
                                    className="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100"
                                    tabIndex="-1"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        setIsComponentVisible(false);
                                        navigate(`/setup/edit-contentful-integration/${handleId}`);
                                    }}
                                >
                                    Edit
                                </a>
                                <a
                                    href="#"
                                    className="text-rose-600 block px-4 py-2 text-sm hover:bg-gray-100"
                                    tabIndex="-1"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        setIsComponentVisible(false);
                                        setDeleteIntegration(true);
                                    }}
                                >
                                    Delete
                                </a>
                                <a
                                    href="#"
                                    className="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100"
                                    tabIndex="-1"
                                    onClick={async (e) => {
                                        e.preventDefault();
                                        setIsComponentVisible(false);
                                        await handleSetAsDefault(handleId);
                                    }}
                                >
                                    Set as default
                                </a>
                            </div>
                        </div>
                    )}
                </AgTableCard>
            )}

            {actionLoading && <Loader />}

            {deleteIntegration && (
                <DeleteIntegrationModal
                    setConfirmModal={setConfirmModal}
                    id={handleId}
                    setDeleteIntegration={setDeleteIntegration}
                    setActionLoading={setActionLoading}
                    getContentfulData={updateContentfulIntegrations}
                />
            )}
            {!loading && confirmModal?.delete && (
                <DeleteModal
                    setConfirmModal={setConfirmModal}
                    setDeleteIntegration={setDeleteIntegration}
                />
            )}
        </div>
    );
};

export default ContentfulIntegrationTable;
